import { accountColorSchema, accountDeletionImpactSchema, accountSchema, accountsBalanceSchema, accountTypeSchema, frequentItemsSchema, parseApiResponse } from "@/api/responseSchemas"
import { apiClient } from '@/api/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/useAuthStore'
import { toLocalYMD } from '@/utils/dateUtils'
import { invalidateFinancialData } from '@/api/queryKeys'
import { captureEntityMutation } from '@/analytics/posthog'

export interface AccountType {
    id: number
    name: string
    created_at: string
    updated_at: string
    deleted_at: string | null
}

export interface AccountColor {
    id: number
    name: string
    hex: string
}

export interface Account {
    id: number
    name: string
    description?: string | null
    initial_balance?: number
    account_type_id: number
    billing_cycle_day?: number
    payment_due_days?: number
    credit_limit?: number
    color_id?: number | null
}

export type FrequentAccounts = Record<string, number>

export interface AccountBalance {
    // matches backend per-account object inside accounts_balance
    monthly_deposit?: number
    monthly_expense?: number
    monthly_balance?: number
    account_id: number
    account_type_id: number
    name: string
    description?: string | null
    credit_limit?: number
    credit_used?: number
    available_credit?: number
}

export interface AccountDeletionImpact {
    account_id: number
    account_name: string
    transactions_to_delete: number
    transfers_as_destination_to_delete: number
    installments_to_delete: number
    import_rows_to_unlink: number
}

// New response shape for /account/balance
export interface AccountsBalanceResponse {
    total_expense: number
    total_deposit: number
    accounts_balance: AccountBalance[]
}

interface AccountInput {
    name: string
    initial_balance?: number
    account_type_id: number
    billing_cycle_day?: number
    payment_due_days?: number
    credit_limit?: number
    color_id?: number | null
}

interface AccountsBalanceQueryParams {
    billable_date_gte?: string
    billable_date_lte?: string
    monthly_expense?: "asc" | "desc" | string
}

export const useAccountsBalance = (params?: AccountsBalanceQueryParams) => {
    const token = useAuthStore((state) => state.token);
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    const effectiveParams: Required<AccountsBalanceQueryParams> = {
        billable_date_gte: params?.billable_date_gte ?? toLocalYMD(startOfMonth),
        billable_date_lte: params?.billable_date_lte ?? toLocalYMD(endOfMonth),
        monthly_expense: params?.monthly_expense ?? "desc",
    }

    return useQuery<AccountsBalanceResponse | undefined>({
        queryKey: ['accountsBalance', effectiveParams],
        queryFn: async () => {
            const res = await apiClient.get('/accounts/balance', {
                params: effectiveParams,
            })
            return parseApiResponse(accountsBalanceSchema, res.data, "GET /accounts/balance")
        },
        enabled: !!token, 
    })
}

export const useAccountTypes = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token);

    return useQuery({
        queryKey: ['accountTypes'],
        queryFn: async () => {
            const res = await apiClient.get('/accounts/types')
            return parseApiResponse(accountTypeSchema.array(), res.data, "GET /accounts/types")
        },
        enabled: !!token && (options?.enabled ?? true),
        staleTime: Infinity,
    })
}

export const useAccountColors = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token);

    return useQuery<AccountColor[]>({
        queryKey: ['accountColors'],
        queryFn: async () => {
            const res = await apiClient.get('/accounts/colors')
            return parseApiResponse(accountColorSchema.array(), res.data, "GET /accounts/colors")
        },
        enabled: !!token && (options?.enabled ?? true),
        staleTime: Infinity,
    })
}

export const useAccounts = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token);

    return useQuery({
        queryKey: ['accounts'],
        queryFn: async () => {
            const res = await apiClient.get('/accounts')
            return parseApiResponse(accountSchema.array(), res.data, "GET /accounts")
        },
        enabled: !!token && (options?.enabled ?? true),
    })
}

export const useFrequentAccounts = (options?: { enabled?: boolean }) => {
    const token = useAuthStore((state) => state.token);

    return useQuery<FrequentAccounts, unknown>({
        queryKey: ['frequentAccounts'],
        queryFn: async () => {
            const res = await apiClient.get('/accounts/frequent')
            return parseApiResponse(frequentItemsSchema, res.data, "GET /accounts/frequent")
        },
        enabled: !!token && (options?.enabled ?? true),
    })
}

const accountDeletionImpactQueryKey = (accountId: number) => ['accountDeletionImpact', accountId] as const

/** Fetches a current account deletion preview only after the user asks to delete. */
export const useAccountDeletionImpact = () => {
    const queryClient = useQueryClient()

    return async (accountId: number) => {
        const queryKey = accountDeletionImpactQueryKey(accountId)
        queryClient.removeQueries({ queryKey, exact: true })

        return queryClient.fetchQuery({
            queryKey,
            queryFn: async () => {
                const res = await apiClient.get(`/accounts/${accountId}/deletion-impact`)
                return parseApiResponse(accountDeletionImpactSchema, res.data, `GET /accounts/${accountId}/deletion-impact`)
            },
            staleTime: 0,
        })
    }
}

// Mutation hooks
export const useCreateAccount = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (accountData: AccountInput) => {
            const dataToSend: Record<string, unknown> = {
                name: accountData.name,
                initial_balance: accountData.initial_balance ?? 0,
                account_type_id: accountData.account_type_id,
                credit_limit: accountData.credit_limit ?? 0,
            }

            if (accountData.color_id !== undefined) dataToSend.color_id = accountData.color_id

            // Only include credit fields if they are provided
            if (accountData.billing_cycle_day !== undefined) {
                dataToSend.billing_cycle_day = accountData.billing_cycle_day
            }
            if (accountData.payment_due_days !== undefined) {
                dataToSend.payment_due_days = accountData.payment_due_days
            }
            const res = await apiClient.post('/accounts', dataToSend)

            return res.data
        },
        onSuccess: () => {
            captureEntityMutation({ entity: 'account', operation: 'create', result: 'success' })
            queryClient.invalidateQueries({ queryKey: ['accounts'] })
            queryClient.invalidateQueries({ queryKey: ['accountsBalance'] })
        },
        onError: (error) => {
            captureEntityMutation({ entity: 'account', operation: 'create', result: 'failure', error })
        },
    })
}

export const useUpdateAccount = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: AccountInput }) => {
            const dataToSend: Record<string, unknown> = {
                name: data.name,
                initial_balance: data.initial_balance ?? 0,
                account_type_id: data.account_type_id,
                credit_limit: data.credit_limit ?? 0,
            }

            if (data.color_id !== undefined) dataToSend.color_id = data.color_id

            // Only include credit fields if they are provided
            if (data.billing_cycle_day !== undefined) {
                dataToSend.billing_cycle_day = data.billing_cycle_day
            }
            if (data.payment_due_days !== undefined) {
                dataToSend.payment_due_days = data.payment_due_days
            }
            const res = await apiClient.patch(`/accounts/${id}`, dataToSend)
            return res.data
        },
        onSuccess: () => {
            captureEntityMutation({ entity: 'account', operation: 'update', result: 'success' })
            queryClient.invalidateQueries({ queryKey: ['accounts'] })
            queryClient.invalidateQueries({ queryKey: ['accountsBalance'] })
        },
        onError: (error) => {
            captureEntityMutation({ entity: 'account', operation: 'update', result: 'failure', error })
        },
    })
}

export const useDeleteAccount = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (accountId: number) => {
            const res = await apiClient.delete(`/accounts/${accountId}`)
            return res.data
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['accounts'] }),
                queryClient.invalidateQueries({ queryKey: ['frequentAccounts'] }),
                invalidateFinancialData(queryClient),
            ])
        },
    })
}
